import { joinUrl } from "./config/serverUrl";

// ============================================================
// DATABASE CONFIGURATION - Unified Event Data Structure
// ============================================================
export const DB_NAME = "EventData";
export const DB_VERSION = 8; // Updated: added EventBodyThumbnails store

// Admin Bodies DB - Shared across all events
export const ADMIN_BODIES_DB_NAME = "AdminBodies_EventData";
export const ADMIN_BODIES_DB_VERSION = 2; // Updated: separate stores per event type

// Store names - Event specific
export const FACE_STORE_NAME = "FaceImages"; // Full face images (existing)
export const BODY_METADATA_STORE = "BodyMetadata"; // Event-specific: selection, logos per body
export const EVENT_BODY_THUMBNAILS_STORE = "EventBodyThumbnails"; // Event-specific: body thumbnails with logos or one-time images
export const FACE_THUMBNAILS_STORE = "FaceThumbnails"; // Face thumbnails (future)
export const RESULTS_STORE_NAME = "ResultThumbnails"; // Moved from separate DB
export const ADMIN_FACES_STORE_NAME = "AdminFaceThumbnails"; // Admin faces thumbnails cache
export const METADATA_STORE_NAME = "MetadataStore"; // ETags and timestamps

// Store names - Admin Bodies (Shared) - Per event type
export const BODY_THUMBNAILS_MINIMOVE = "BodyThumbnails_minimove";
export const BODY_THUMBNAILS_SWINGIES = "BodyThumbnails_swingies";

// Helper to get the correct store name for an event type
export const getBodyThumbnailsStore = (eventType) => {
  switch (eventType?.toLowerCase()) {
    case "minimove":
      return BODY_THUMBNAILS_MINIMOVE;
    case "swingies":
      return BODY_THUMBNAILS_SWINGIES;
    default:
      throw new Error(`Unknown event type: ${eventType}`);
  }
};

// Legacy store names (kept for backward compatibility)
export const BODY_STORE_NAME = "BodyImages"; // Old, not used
export const LAST_UPDATE_STORE_NAME = "LastUpdate"; // Old, replaced by MetadataStore

// TTL for cached data - 5 hours for admin with auto-refresh on access (touch mechanism)
// Attraction flow (faces during event) does NOT use TTL - data is deleted when event ends
export const CACHE_TTL = {
  ADMIN: 5 * 60 * 60 * 1000, // 5 hours for admin/library
  EVENT_BODIES: 5 * 60 * 60 * 1000, // 5 hours for event bodies metadata
  RESULTS: 5 * 60 * 60 * 1000, // 5 hours for results thumbnails
  DEFAULT: 5 * 60 * 60 * 1000, // 5 hours default
};

// Helper to get TTL based on store type (for admin stores only)
// Note: FACE_STORE_NAME does NOT have TTL - it's managed by event lifecycle
export const getCacheTTL = (storeName) => {
  switch (storeName) {
    case BODY_THUMBNAILS_MINIMOVE:
    case BODY_THUMBNAILS_SWINGIES:
    case BODY_METADATA_STORE:
      return CACHE_TTL.EVENT_BODIES;
    case RESULTS_STORE_NAME:
    case ADMIN_FACES_STORE_NAME:
      return CACHE_TTL.RESULTS;
    default:
      return CACHE_TTL.DEFAULT;
  }
};

// ============================================================
// TTL TOUCH MECHANISM - Update timestamp on access
// ============================================================

/**
 * Update timestamp of an entry when it's accessed (read)
 * This extends the TTL by resetting the timer
 * @param {IDBDatabase} db - Open database connection
 * @param {string} storeName - Store name
 * @param {any} key - Entry key
 * @param {object} entry - Entry object to update
 * @returns {Promise<boolean>} - Success status
 */
const touchTimestamp = async (db, storeName, key, entry) => {
  try {
    const transaction = db.transaction(storeName, "readwrite");
    const store = transaction.objectStore(storeName);

    // Update only the timestamp field
    const updatedEntry = {
      ...entry,
      timestamp: new Date().toISOString(),
    };

    store.put(updatedEntry);

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve(true);
      transaction.onerror = (event) => {
        console.warn(
          `Failed to touch timestamp for ${storeName}:`,
          event.target.errorCode
        );
        reject(event.target.errorCode);
      };
    });
  } catch (err) {
    console.warn(`Error touching timestamp for ${storeName}:`, err);
    return false;
  }
};

// ============================================================
// AUTO CLEANUP - Run cleanup automatically on DB open
// ============================================================

// Track last cleanup time to avoid running too frequently
const lastCleanupTimes = new Map();
const CLEANUP_THROTTLE = 5 * 60 * 1000; // Run cleanup max once per 5 minutes

/**
 * Run automatic cleanup for expired entries
 * Throttled to avoid running too frequently
 * @param {string} dbIdentifier - Unique DB identifier
 * @param {Function} cleanupFn - Cleanup function to run
 */
const runAutoCleanup = (dbIdentifier, cleanupFn) => {
  const now = Date.now();
  const lastCleanup = lastCleanupTimes.get(dbIdentifier) || 0;

  // Skip if cleaned up recently
  if (now - lastCleanup < CLEANUP_THROTTLE) {
    return;
  }

  // Update last cleanup time
  lastCleanupTimes.set(dbIdentifier, now);

  // Run cleanup in background (non-blocking)
  cleanupFn().catch((err) => {
    console.warn(`Auto cleanup failed for ${dbIdentifier}:`, err);
  });
};

// Function to get the database name based on the event name
export const getDbName = (eventName) => `${eventName}_${DB_NAME}`;

// Open AdminBodies DB (shared across all events)
export const openAdminBodiesDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(
      ADMIN_BODIES_DB_NAME,
      ADMIN_BODIES_DB_VERSION
    );

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create separate Body Thumbnails stores per event type
      if (!db.objectStoreNames.contains(BODY_THUMBNAILS_MINIMOVE)) {
        db.createObjectStore(BODY_THUMBNAILS_MINIMOVE, { keyPath: "name" });
      }
      if (!db.objectStoreNames.contains(BODY_THUMBNAILS_SWINGIES)) {
        db.createObjectStore(BODY_THUMBNAILS_SWINGIES, { keyPath: "name" });
      }

      // Create Metadata store for ETag and last refresh per event type
      if (!db.objectStoreNames.contains(METADATA_STORE_NAME)) {
        db.createObjectStore(METADATA_STORE_NAME, { keyPath: "store_name" });
      }
    };

    request.onsuccess = (event) => {
      const db = event.target.result;

      // Run auto cleanup for expired thumbnails (throttled)
      runAutoCleanup(ADMIN_BODIES_DB_NAME, () =>
        cleanUpOldBodyThumbnails(null, CACHE_TTL.ADMIN)
      );

      resolve(db);
    };

    request.onerror = (event) => {
      console.error(
        "Error opening AdminBodies database:",
        event.target.errorCode
      );
      reject(event.target.errorCode);
    };
  });
};

export const openDatabase = (eventName) => {
  const dbName = getDbName(eventName);
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(dbName, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Delete legacy BodyThumbnails store if it exists (moved to AdminBodies DB)
      const LEGACY_BODY_THUMBNAILS_STORE = "BodyThumbnails";
      if (db.objectStoreNames.contains(LEGACY_BODY_THUMBNAILS_STORE)) {
        db.deleteObjectStore(LEGACY_BODY_THUMBNAILS_STORE);
      }

      // Create Face Images store (existing functionality)
      if (!db.objectStoreNames.contains(FACE_STORE_NAME)) {
        db.createObjectStore(FACE_STORE_NAME, { keyPath: "path" });
      }

      // Create Body Metadata store (event-specific: selection, logos)
      if (!db.objectStoreNames.contains(BODY_METADATA_STORE)) {
        db.createObjectStore(BODY_METADATA_STORE, { keyPath: "id" });
      }

      // Create Event Body Thumbnails store (event-specific: logos and one-time images)
      if (!db.objectStoreNames.contains(EVENT_BODY_THUMBNAILS_STORE)) {
        db.createObjectStore(EVENT_BODY_THUMBNAILS_STORE, { keyPath: "name" });
      }

      // Create Face Thumbnails store (for future use)
      if (!db.objectStoreNames.contains(FACE_THUMBNAILS_STORE)) {
        db.createObjectStore(FACE_THUMBNAILS_STORE, { keyPath: "name" });
      }

      // Create Results Thumbnails store (moved from separate DB)
      if (!db.objectStoreNames.contains(RESULTS_STORE_NAME)) {
        db.createObjectStore(RESULTS_STORE_NAME, { keyPath: "path" });
      }

      // Create Admin Faces Thumbnails store (for admin panel faces management)
      if (!db.objectStoreNames.contains(ADMIN_FACES_STORE_NAME)) {
        db.createObjectStore(ADMIN_FACES_STORE_NAME, { keyPath: "path" });
      }

      // Create general Metadata store (ETags and timestamps)
      if (!db.objectStoreNames.contains(METADATA_STORE_NAME)) {
        db.createObjectStore(METADATA_STORE_NAME, { keyPath: "store_name" });
      }

      // Legacy stores (kept for backward compatibility)
      if (!db.objectStoreNames.contains(BODY_STORE_NAME)) {
        db.createObjectStore(BODY_STORE_NAME, { keyPath: "path" });
      }
      if (!db.objectStoreNames.contains(LAST_UPDATE_STORE_NAME)) {
        db.createObjectStore(LAST_UPDATE_STORE_NAME, { keyPath: "storeName" });
      }
    };

    request.onsuccess = (event) => {
      const db = event.target.result;
      // No auto-cleanup here - attraction flow needs maximum speed
      // Cleanup is handled explicitly:
      // - Attraction data: cleared when event ends (clearAttractionStores)
      // - Admin data: TTL managed by touchTimestamp in admin functions
      resolve(db);
    };

    request.onerror = (event) => {
      console.error("Error opening database:", event.target.errorCode);
      reject(event.target.errorCode);
    };
  });
};

export const saveFullImageToDB = async (
  eventName,
  storeName,
  path,
  imageData
) => {
  const db = await openDatabase(eventName);
  const transaction = db.transaction(storeName, "readwrite");
  const store = transaction.objectStore(storeName);

  return new Promise((resolve, reject) => {
    store.put({ path, imageData, timestamp: new Date().toISOString() });

    transaction.oncomplete = () => resolve(true);
    transaction.onerror = (event) => reject(event.target.errorCode);
  });
};

/**
 * Migrate results thumbnails from old separate DB to main event DB
 * This function checks if an old results DB exists and migrates its data
 * @param {string} eventName - The event name
 */
export const migrateResultsThumbnailsDB = async (eventName) => {
  const oldDbName = `${eventName}_ResultsThumbnails`;

  try {
    // First, check if the DB exists WITHOUT opening it (to avoid creating it)
    const databases = await indexedDB.databases();
    const dbExists = databases.some((db) => db.name === oldDbName);

    if (!dbExists) {
      // DB doesn't exist - nothing to migrate, don't create it
      return { migrated: false, message: "Old DB doesn't exist" };
    }

    // DB exists - now we can safely open it for migration
    const oldDbRequest = indexedDB.open(oldDbName, 1);

    return new Promise((resolve) => {
      oldDbRequest.onsuccess = async (event) => {
        const oldDb = event.target.result;

        // Check if old DB has the RESULTS_STORE_NAME
        if (!oldDb.objectStoreNames.contains("ResultThumbnails")) {
          oldDb.close();
          resolve({
            migrated: false,
            message: "Old DB doesn't have results store",
          });
          return;
        }

        // Read all data from old DB
        const transaction = oldDb.transaction("ResultThumbnails", "readonly");
        const store = transaction.objectStore("ResultThumbnails");
        const getAllRequest = store.getAll();

        getAllRequest.onsuccess = async () => {
          const allData = getAllRequest.result;
          oldDb.close();

          if (allData.length === 0) {
            resolve({ migrated: false, message: "No data to migrate" });
            return;
          }

          // Write all data to new DB
          const newDb = await openDatabase(eventName);
          const newTransaction = newDb.transaction(
            RESULTS_STORE_NAME,
            "readwrite"
          );
          const newStore = newTransaction.objectStore(RESULTS_STORE_NAME);

          allData.forEach((item) => {
            newStore.put(item);
          });

          newTransaction.oncomplete = async () => {
            // Delete old DB after successful migration
            const deleteRequest = indexedDB.deleteDatabase(oldDbName);
            deleteRequest.onsuccess = () => {
              resolve({
                migrated: true,
                itemCount: allData.length,
                message: `Successfully migrated ${allData.length} thumbnails`,
              });
            };
            deleteRequest.onerror = () => {
              console.warn(`Failed to delete old DB ${oldDbName}`);
              resolve({
                migrated: true,
                itemCount: allData.length,
                message: `Migrated ${allData.length} thumbnails, but failed to delete old DB`,
              });
            };
          };

          newTransaction.onerror = () => {
            console.error("Failed to write to new DB");
            resolve({ migrated: false, message: "Failed to write to new DB" });
          };
        };

        getAllRequest.onerror = () => {
          oldDb.close();
          resolve({ migrated: false, message: "Failed to read from old DB" });
        };
      };

      oldDbRequest.onerror = () => {
        // Old DB doesn't exist, nothing to migrate
        resolve({ migrated: false, message: "Old DB doesn't exist" });
      };
    });
  } catch (error) {
    return { migrated: false, message: error.message };
  }
};

/**
 * Save a result thumbnail to the event's main database
 * @param {string} eventName - The event name
 * @param {string} path - The image path
 * @param {string} dataUri - The thumbnail data URI
 * @param {string} modified - The modification timestamp
 */
export const saveResultThumbnailToDB = async (
  eventName,
  path,
  dataUri,
  modified
) => {
  const db = await openDatabase(eventName);
  const transaction = db.transaction(RESULTS_STORE_NAME, "readwrite");
  const store = transaction.objectStore(RESULTS_STORE_NAME);

  return new Promise((resolve, reject) => {
    store.put({
      path,
      dataUri,
      modified,
      timestamp: new Date().toISOString(),
    });

    transaction.oncomplete = () => resolve(true);
    transaction.onerror = (event) => reject(event.target.errorCode);
  });
};

/**
 * Get a result thumbnail from the event's main database
 * @param {string} eventName - The event name
 * @param {string} path - The image path
 */
export const getResultThumbnailFromDB = async (eventName, path) => {
  const db = await openDatabase(eventName);
  const transaction = db.transaction(RESULTS_STORE_NAME, "readonly");
  const store = transaction.objectStore(RESULTS_STORE_NAME);
  const request = store.get(path);

  return new Promise((resolve, reject) => {
    request.onsuccess = (event) => {
      const result = event.target.result || null;

      // Touch timestamp to extend TTL on access (non-blocking, don't await)
      if (result) {
        touchTimestamp(db, RESULTS_STORE_NAME, path, result).catch((err) => {
          console.warn("Failed to touch timestamp:", err);
        });
      }

      resolve(result);
    };
    request.onerror = (event) => {
      console.error("Error fetching result thumbnail:", event.target.errorCode);
      reject(event.target.errorCode);
    };
  });
};

/**
 * Clean up old result thumbnails from the event's main database
 * @param {string} eventName - The event name
 * @param {number} ttl - Time to live in milliseconds
 */
export const cleanUpOldResultThumbnails = async (eventName, ttl) => {
  const db = await openDatabase(eventName);
  const now = new Date();

  const transaction = db.transaction(RESULTS_STORE_NAME, "readwrite");
  const store = transaction.objectStore(RESULTS_STORE_NAME);

  const allKeysRequest = store.getAllKeys();

  allKeysRequest.onsuccess = async (event) => {
    const allKeys = event.target.result;
    const cleanupPromises = allKeys.map((key) => {
      return new Promise((resolve, reject) => {
        const getRequest = store.get(key);
        getRequest.onsuccess = (e) => {
          const entry = e.target.result;
          if (entry) {
            const entryTime = new Date(entry.timestamp);
            if (now - entryTime > ttl) {
              const deleteRequest = store.delete(key);
              deleteRequest.onsuccess = () => resolve(true);
              deleteRequest.onerror = (err) => reject(err.target.errorCode);
            } else {
              resolve(true);
            }
          } else {
            resolve(false);
          }
        };
        getRequest.onerror = (err) => reject(err.target.errorCode);
      });
    });

    try {
      await Promise.all(cleanupPromises);
      if (typeof transaction.commit === "function") {
        transaction.commit();
      }
    } catch (error) {
      console.error("Error during results cleanup promises:", error);
    }
  };

  allKeysRequest.onerror = (event) => {
    console.error(
      "Failed to fetch keys from results DB:",
      event.target.errorCode
    );
  };
};

// ============================================================
// ADMIN FACES THUMBNAILS MANAGEMENT
// ============================================================

/**
 * Save an admin face thumbnail to the event's database
 * @param {string} eventName - The event name
 * @param {string} path - The image path (key)
 * @param {object} faceData - Object containing thumbnail and metadata
 * @param {string} faceData.name - File name
 * @param {string} faceData.thumbnail - Base64 encoded thumbnail
 * @param {number} faceData.width - Thumbnail width
 * @param {number} faceData.height - Thumbnail height
 * @param {string} faceData.folderKey - Folder key (ready, failure, etc.)
 */
export const saveAdminFaceThumbnailToDB = async (eventName, path, faceData) => {
  const db = await openDatabase(eventName);
  const transaction = db.transaction(ADMIN_FACES_STORE_NAME, "readwrite");
  const store = transaction.objectStore(ADMIN_FACES_STORE_NAME);

  return new Promise((resolve, reject) => {
    store.put({
      path,
      name: faceData.name,
      thumbnail: faceData.thumbnail,
      width: faceData.width,
      height: faceData.height,
      folderKey: faceData.folderKey,
      timestamp: new Date().toISOString(),
    });

    transaction.oncomplete = () => resolve(true);
    transaction.onerror = (event) => reject(event.target.errorCode);
  });
};

/**
 * Get an admin face thumbnail from the event's database
 * @param {string} eventName - The event name
 * @param {string} path - The image path
 */
export const getAdminFaceThumbnailFromDB = async (eventName, path) => {
  const db = await openDatabase(eventName);
  const transaction = db.transaction(ADMIN_FACES_STORE_NAME, "readonly");
  const store = transaction.objectStore(ADMIN_FACES_STORE_NAME);
  const request = store.get(path);

  return new Promise((resolve, reject) => {
    request.onsuccess = (event) => {
      const result = event.target.result || null;

      // Touch timestamp to extend TTL on access (non-blocking, don't await)
      if (result) {
        touchTimestamp(db, ADMIN_FACES_STORE_NAME, path, result).catch(
          (err) => {
            console.warn("Failed to touch timestamp:", err);
          }
        );
      }

      resolve(result);
    };
    request.onerror = (event) => {
      console.error(
        "Error fetching admin face thumbnail:",
        event.target.errorCode
      );
      reject(event.target.errorCode);
    };
  });
};

/**
 * Get all admin face thumbnails from the event's database
 * @param {string} eventName - The event name
 */
export const getAllAdminFaceThumbnailsFromDB = async (eventName) => {
  const db = await openDatabase(eventName);
  const transaction = db.transaction(ADMIN_FACES_STORE_NAME, "readonly");
  const store = transaction.objectStore(ADMIN_FACES_STORE_NAME);
  const request = store.getAll();

  return new Promise((resolve, reject) => {
    request.onsuccess = (event) => {
      resolve(event.target.result || []);
    };
    request.onerror = (event) => {
      console.error(
        "Error fetching all admin face thumbnails:",
        event.target.errorCode
      );
      reject(event.target.errorCode);
    };
  });
};

/**
 * Save admin faces metadata (ETag and folder structure) to IndexedDB
 * @param {string} eventName - The event name
 * @param {object} metadata - Metadata object
 * @param {string} metadata.etag - ETag from server
 * @param {Array} metadata.folders - Array of folder objects with counts
 */
export const saveAdminFacesMetadata = async (eventName, metadata) => {
  const db = await openDatabase(eventName);
  const transaction = db.transaction(METADATA_STORE_NAME, "readwrite");
  const store = transaction.objectStore(METADATA_STORE_NAME);

  const dataToStore = {
    store_name: ADMIN_FACES_STORE_NAME,
    etag: metadata.etag || null,
    folders: metadata.folders || [],
    last_modified: new Date().toISOString(),
  };

  return new Promise((resolve, reject) => {
    store.put(dataToStore);
    transaction.oncomplete = () => resolve(true);
    transaction.onerror = (event) => {
      console.error(
        "Error saving admin faces metadata:",
        event.target.errorCode
      );
      reject(event.target.errorCode);
    };
  });
};

/**
 * Get admin faces metadata from IndexedDB
 * @param {string} eventName - The event name
 */
export const getAdminFacesMetadata = async (eventName) => {
  const db = await openDatabase(eventName);
  const transaction = db.transaction(METADATA_STORE_NAME, "readonly");
  const store = transaction.objectStore(METADATA_STORE_NAME);
  const request = store.get(ADMIN_FACES_STORE_NAME);

  return new Promise((resolve, reject) => {
    request.onsuccess = (event) => {
      resolve(event.target.result || null);
    };
    request.onerror = (event) => {
      console.error(
        "Error fetching admin faces metadata:",
        event.target.errorCode
      );
      reject(event.target.errorCode);
    };
  });
};

/**
 * Delete admin face thumbnails that are no longer in the folder list
 * @param {string} eventName - The event name
 * @param {Set} validPaths - Set of valid paths that should be kept
 */
export const cleanupRemovedAdminFaces = async (eventName, validPaths) => {
  const db = await openDatabase(eventName);
  const transaction = db.transaction(ADMIN_FACES_STORE_NAME, "readwrite");
  const store = transaction.objectStore(ADMIN_FACES_STORE_NAME);
  const allKeysRequest = store.getAllKeys();

  return new Promise((resolve, reject) => {
    allKeysRequest.onsuccess = async () => {
      const allKeys = allKeysRequest.result;
      let deletedCount = 0;

      for (const key of allKeys) {
        if (!validPaths.has(key)) {
          store.delete(key);
          deletedCount++;
        }
      }

      transaction.oncomplete = () => {
        resolve(deletedCount);
      };

      transaction.onerror = (event) => {
        console.error("Error cleaning up admin faces:", event.target.errorCode);
        reject(event.target.errorCode);
      };
    };

    allKeysRequest.onerror = (event) => {
      console.error("Error fetching keys for cleanup:", event.target.errorCode);
      reject(event.target.errorCode);
    };
  });
};

/**
 * Clean up old admin face thumbnails based on TTL
 * @param {string} eventName - The event name
 * @param {number} ttl - Time to live in milliseconds (default 30 minutes)
 */
export const cleanUpOldAdminFaceThumbnails = async (
  eventName,
  ttl = CACHE_TTL.DEFAULT
) => {
  const db = await openDatabase(eventName);
  const now = new Date();

  const transaction = db.transaction(ADMIN_FACES_STORE_NAME, "readwrite");
  const store = transaction.objectStore(ADMIN_FACES_STORE_NAME);

  const allKeysRequest = store.getAllKeys();

  allKeysRequest.onsuccess = async (event) => {
    const allKeys = event.target.result;
    const cleanupPromises = allKeys.map((key) => {
      return new Promise((resolve, reject) => {
        const getRequest = store.get(key);
        getRequest.onsuccess = (e) => {
          const entry = e.target.result;
          if (entry && entry.timestamp) {
            const entryTime = new Date(entry.timestamp);
            if (now - entryTime > ttl) {
              const deleteRequest = store.delete(key);
              deleteRequest.onsuccess = () => resolve(true);
              deleteRequest.onerror = (err) => reject(err.target.errorCode);
            } else {
              resolve(true);
            }
          } else {
            resolve(false);
          }
        };
        getRequest.onerror = (err) => reject(err.target.errorCode);
      });
    });

    try {
      await Promise.all(cleanupPromises);
      if (typeof transaction.commit === "function") {
        transaction.commit();
      }
    } catch (error) {
      console.error("Error during admin faces cleanup:", error);
    }
  };

  allKeysRequest.onerror = (event) => {
    console.error(
      "Failed to fetch keys from admin faces DB:",
      event.target.errorCode
    );
  };
};

export const clearStore = async (eventName, storeName) => {
  const db = await openDatabase(eventName);
  const transaction = db.transaction(storeName, "readwrite");
  const store = transaction.objectStore(storeName);

  return new Promise((resolve, reject) => {
    const request = store.clear();
    request.onsuccess = () => resolve(true);
    request.onerror = (event) => reject(event.target.errorCode);
  });
};

export const updateLastUpdateTime = async (eventName, storeName) => {
  const db = await openDatabase(eventName);
  const transaction = db.transaction([LAST_UPDATE_STORE_NAME], "readwrite");
  const store = transaction.objectStore(LAST_UPDATE_STORE_NAME);
  const timestamp = new Date().toISOString();
  store.put({ storeName, timestamp });
  return await new Promise((resolve, reject) => {
    transaction.oncomplete = () => {
      resolve(true);
    };
    transaction.onerror = (event) => {
      console.error(
        "Transaction error on updating time:",
        event.target.errorCode
      );
      reject(event.target.errorCode);
    };
  });
};

export const getLastUpdateTime = async (eventName, storeName) => {
  const db = await openDatabase(eventName);
  const transaction = db.transaction([LAST_UPDATE_STORE_NAME], "readonly");
  const store = transaction.objectStore(LAST_UPDATE_STORE_NAME);
  const request = store.get(storeName);
  return await new Promise((resolve, reject) => {
    request.onsuccess = (event) => {
      if (event.target.result) {
        resolve(event.target.result.timestamp);
      } else {
        resolve(null);
      }
    };
    request.onerror = (event) => {
      console.error("Error fetching last update time:", event.target.errorCode);
      reject(event.target.errorCode);
    };
  });
};

export const getAllKeysFromDB = async (eventName, storeName) => {
  const db = await openDatabase(eventName);
  const transaction = db.transaction(storeName, "readonly");
  const store = transaction.objectStore(storeName);

  return new Promise((resolve, reject) => {
    const request = store.getAllKeys();
    request.onsuccess = (event) => {
      resolve(event.target.result);
    };
    request.onerror = (event) => {
      console.error(
        "Error fetching keys from IndexedDB:",
        event.target.errorCode
      );
      reject(event.target.errorCode);
    };
  });
};

/**
 * Deletes all event databases by scanning for all databases.
 * Handles both old format (_ImageDatabase) and new format (_EventData)
 * NOTE: Does NOT delete AdminBodies_EventData (shared across all events)
 */
export const deleteAllEventDatabases = async () => {
  // Get all available database names in the browser
  const dbNames = await window.indexedDB.databases();

  // Filter for our event databases - both old and new formats
  const eventDbs = dbNames.filter(
    (db) =>
      db.name &&
      (db.name.includes("_ImageDatabase") || // Old format (MASTER)
        (db.name.includes("_EventData") && db.name !== ADMIN_BODIES_DB_NAME)) // New format, but NOT shared AdminBodies
  );

  // Legacy results databases
  const resultDbs = dbNames.filter(
    (db) => db.name && db.name.includes("_ResultsThumbnails")
  );

  // console.log(`[deleteAllEventDatabases] Deleting ${eventDbs.length} event DBs`);

  // Delete each event database
  for (const db of eventDbs) {
    await deleteDatabase(db.name);
  }

  for (const db of resultDbs) {
    await deleteDatabase(db.name);
  }
};

/**
 * Delete a specific event's database completely
 * Use this when an event is deleted from the system
 * @param {string} eventName - The event name
 */
export const deleteEventDatabase = async (eventName) => {
  const dbName = getDbName(eventName);
  const oldDbName = `${eventName}_ImageDatabase`; // Legacy format
  const resultsDbName = `${eventName}_ResultsThumbnails`; // Legacy results

  const dbNames = await window.indexedDB.databases();
  const dbsToDelete = dbNames.filter(
    (db) =>
      db.name === dbName || db.name === oldDbName || db.name === resultsDbName
  );

  // console.log(`[deleteEventDatabase] Deleting databases for event ${eventName}`);

  for (const db of dbsToDelete) {
    await deleteDatabase(db.name);
  }

  return { deleted: dbsToDelete.length };
};

/**
 * Delete IndexedDB databases for events that no longer exist
 * Called on page load/refresh to clean up databases for deleted or expired events
 * @param {string} serverUrl - The server URL to fetch event list from
 */
export const deleteExpiredEventDatabases = async (serverUrl) => {
  // console.log(`[deleteExpiredEventDatabases] Starting cleanup...`);
  try {
    // Fetch all valid event names from the server
    const response = await fetch(joinUrl(serverUrl, "/api/events-managements"));
    if (!response.ok) {
      console.error(
        `[deleteExpiredEventDatabases] Failed to fetch events list: ${response.status}`
      );
      return { error: "Failed to fetch events list", deleted: 0 };
    }

    const data = await response.json();
    const validEventNames = data.events || [];

    // console.log(`[deleteExpiredEventDatabases] Valid events:`, validEventNames.length);

    // Get all indexedDB databases in the browser
    const dbNames = await window.indexedDB.databases();

    // Filter for event databases (exclude AdminBodies and other shared databases)
    const eventDbs = dbNames.filter(
      (db) =>
        db.name &&
        (db.name.includes("_ImageDatabase") || // Old format
          (db.name.includes("_EventData") && db.name !== ADMIN_BODIES_DB_NAME))
    );

    // Legacy results databases
    const resultDbs = dbNames.filter(
      (db) => db.name && db.name.includes("_ResultsThumbnails")
    );

    const allEventDbs = [...eventDbs, ...resultDbs];

    // console.log(`[deleteExpiredEventDatabases] Found ${allEventDbs.length} event databases`);

    // Extract event name from database name and check if it exists
    const dbsToDelete = [];
    for (const db of allEventDbs) {
      let eventName;

      // Extract event name from database name
      if (db.name.includes("_ImageDatabase")) {
        // Old format: {eventName}_ImageDatabase
        eventName = db.name.replace("_ImageDatabase", "");
      } else if (db.name.includes("_ResultsThumbnails")) {
        // Legacy results: {eventName}_ResultsThumbnails
        eventName = db.name.replace("_ResultsThumbnails", "");
      } else if (db.name.includes("_EventData")) {
        // New format: {eventName}_EventData
        eventName = db.name.replace("_EventData", "");
      }

      // Check if this event still exists on the server
      if (eventName && !validEventNames.includes(eventName)) {
        dbsToDelete.push(db);
      }
    }

    // Delete expired databases
    for (const db of dbsToDelete) {
      await deleteDatabase(db.name);
    }

    return { deleted: dbsToDelete.length };
  } catch (error) {
    console.error(
      `[deleteExpiredEventDatabases] Error cleaning up expired databases:`,
      error
    );
    return { error: error.message, deleted: 0 };
  }
};

export const deleteImageFromDB = async (eventName, storeName, path) => {
  const db = await openDatabase(eventName);
  const transaction = db.transaction(storeName, "readwrite");
  const store = transaction.objectStore(storeName);

  return new Promise((resolve, reject) => {
    const request = store.delete(path);
    request.onsuccess = () => {
      resolve(true);
    };
    request.onerror = (event) => {
      console.error(
        `Error deleting image from ${storeName}:`,
        event.target.errorCode
      );
      reject(event.target.errorCode);
    };
  });
};

export const deleteSelectedImages = async (
  eventName,
  storeName,
  selectedImages
) => {
  try {
    await Promise.all(
      selectedImages.map((path) =>
        deleteImageFromDB(eventName, storeName, path)
      )
    );
  } catch (error) {
    console.error("Error deleting selected images:", error);
  }
};

// An entry is expired when it is older than `ttl` (ms) at `now`. With
// `keepFrom`, an entry stamped at or after that moment is never expired: it
// was written by the refresh the purge follows.
export const isEntryExpired = (entry, now, ttl, keepFrom = null) => {
  const entryTime = new Date(entry.timestamp);
  if (keepFrom != null && !(entryTime < new Date(keepFrom))) return false;
  return now - entryTime > ttl;
};

export const cleanUpOldEntries = async (
  eventName,
  storeName,
  ttl,
  { keepFrom = null } = {}
) => {
  const db = await openDatabase(eventName);
  const now = new Date();

  const transaction = db.transaction(storeName, "readwrite");
  const store = transaction.objectStore(storeName);

  const allKeysRequest = store.getAllKeys();

  allKeysRequest.onsuccess = async (event) => {
    const allKeys = event.target.result;
    const cleanupPromises = allKeys.map((key) => {
      return new Promise((resolve, reject) => {
        const getRequest = store.get(key);
        getRequest.onsuccess = (e) => {
          const entry = e.target.result;
          if (entry) {
            if (isEntryExpired(entry, now, ttl, keepFrom)) {
              const deleteRequest = store.delete(key);
              deleteRequest.onsuccess = () => resolve(true);
              deleteRequest.onerror = (err) => reject(err.target.errorCode);
            } else {
              resolve(true);
            }
          } else {
            resolve(false); // If no entry, resolve to false indicating nothing to delete
          }
        };
        getRequest.onerror = (err) => reject(err.target.errorCode);
      });
    });

    try {
      await Promise.all(cleanupPromises);
      if (typeof transaction.commit === "function") {
        transaction.commit();
      }
    } catch (error) {
      console.error("Error during cleanup promises:", error);
    }
  };

  allKeysRequest.onerror = (event) => {
    console.error("Failed to fetch keys:", event.target.errorCode);
  };
};

/**
 * Deletes the entire IndexedDB database.
 * @param {string} dbName The name of the database to delete.
 */
export const deleteDatabase = async (dbName) => {
  return new Promise((resolve, reject) => {
    const deleteRequest = indexedDB.deleteDatabase(dbName);

    deleteRequest.onsuccess = () => {
      resolve();
    };

    deleteRequest.onerror = (event) => {
      console.error(
        `Error deleting database ${dbName}:`,
        event.target.errorCode
      );
      reject(event.target.errorCode);
    };

    deleteRequest.onblocked = () => {
      console.warn(`Delete operation of database ${dbName} blocked.`);
    };
  });
};

// Extended function to support face position
export const saveImageBlobToDB = async (
  eventName,
  storeName,
  path,
  imageBlob,
  width = 0,
  height = 0,
  face_position_ratio = null
) => {
  const db = await openDatabase(eventName);
  const transaction = db.transaction(storeName, "readwrite");
  const store = transaction.objectStore(storeName);

  return await new Promise((resolve, reject) => {
    try {
      const dataToStore = {
        path,
        imageData: imageBlob,
        timestamp: new Date().toISOString(),
        width,
        height,
      };

      // Add face position if available
      if (face_position_ratio) {
        dataToStore.face_position_ratio = face_position_ratio;
      }

      store.put(dataToStore);
      transaction.oncomplete = () => resolve(true);
      transaction.onerror = (event) => reject(event.target.errorCode);
    } catch (error) {
      console.error("Error saving image blob to DB:", error);
      reject(error);
    }
  });
};

// Fast read for attraction flow - no TTL overhead
// Returns full object including metadata (face_position_ratio, width, height)
export const getFullObjectFromDB = async (eventName, storeName, path) => {
  const db = await openDatabase(eventName);
  const transaction = db.transaction(storeName, "readonly");
  const store = transaction.objectStore(storeName);

  return await new Promise((resolve, reject) => {
    const request = store.get(path);
    request.onsuccess = (event) => {
      resolve(event.target.result || null);
    };
    request.onerror = (event) => {
      console.error(
        "Error fetching full object from DB:",
        event.target.errorCode
      );
      reject(event.target.errorCode);
    };
  });
};

// Fast read for attraction flow - no TTL overhead
export const getImageBlobFromDB = async (eventName, storeName, path) => {
  const db = await openDatabase(eventName);
  const transaction = db.transaction(storeName, "readonly");
  const store = transaction.objectStore(storeName);

  return await new Promise((resolve, reject) => {
    const request = store.get(path);
    request.onsuccess = (event) => {
      const entry = event.target.result;
      if (entry) {
        resolve(entry.imageData);
      } else {
        resolve(null);
      }
    };
    request.onerror = (event) => {
      console.error(
        "Error fetching image blob from DB:",
        event.target.errorCode
      );
      reject(event.target.errorCode);
    };
  });
};

// ============================================================
// BODY THUMBNAILS MANAGEMENT (New IndexedDB approach)
// ============================================================

/**
 * Save multiple body thumbnails to AdminBodies DB (shared library)
 * Efficient bulk save operation
 * @param {string} eventType - Event type (minimove/swingies/pixelme)
 * @param {Array} bodyDataArray - Array of body data objects with thumbnails
 */
export const saveAdminBodyThumbnails = async (eventType, bodyDataArray) => {
  if (!bodyDataArray || bodyDataArray.length === 0) {
    console.warn("[IndexedDB] saveAdminBodyThumbnails called with empty array");
    return { savedCount: 0, errorCount: 0, skippedCount: 0 };
  }

  const storeName = getBodyThumbnailsStore(eventType);
  const db = await openAdminBodiesDB();

  // First, read all existing entries to check which ones should be updated
  const readTransaction = db.transaction(storeName, "readonly");
  const readStore = readTransaction.objectStore(storeName);

  // Get all existing entries
  const existingEntries = await new Promise((resolve) => {
    const request = readStore.getAll();
    request.onsuccess = () => {
      const entries = request.result || [];
      const map = new Map();
      entries.forEach(entry => {
        if (entry && entry.name) {
          map.set(entry.name, entry);
        }
      });
      resolve(map);
    };
    request.onerror = () => resolve(new Map());
  });

  // Filter bodies that should be saved (skip null thumbnails if existing has data)
  const bodiesToSave = [];
  let skippedCount = 0;

  bodyDataArray.forEach((bodyData) => {
    if (!bodyData || !bodyData.name) {
      skippedCount++;
      return;
    }

    const newHasThumbnail = Boolean(bodyData.thumbnail_data_uri);
    const existing = existingEntries.get(bodyData.name);
    const existingHasThumbnail = Boolean(existing?.thumbnail_data_uri);

    // Decision logic (same as saveAdminBodyThumbnail):
    // - If no existing entry: only save if new has thumbnail
    // - If existing has thumbnail but new doesn't: skip (don't overwrite with null)
    // - If new has thumbnail: save
    // - If neither has thumbnail: skip

    if (!existing) {
      // No existing entry - only save if we have actual thumbnail
      if (newHasThumbnail) {
        bodiesToSave.push(bodyData);
      } else {
        skippedCount++;
      }
    } else if (!newHasThumbnail && existingHasThumbnail) {
      // Don't overwrite good data with null thumbnail
      skippedCount++;
    } else if (newHasThumbnail) {
      // New has thumbnail - save
      bodiesToSave.push(bodyData);
    } else {
      // Neither has thumbnail - skip
      skippedCount++;
    }
  });

  if (bodiesToSave.length === 0) {
    return { savedCount: 0, errorCount: 0, skippedCount };
  }

  // Now save the filtered bodies
  const writeTransaction = db.transaction(storeName, "readwrite");
  const writeStore = writeTransaction.objectStore(storeName);

  const timestamp = new Date().toISOString();
  let savedCount = 0;
  let errorCount = 0;

  // Queue all put operations
  bodiesToSave.forEach((bodyData) => {
    const dataToStore = {
      name: bodyData.name,
      thumbnail_data_uri: bodyData.thumbnail_data_uri || null,
      modified: bodyData.modified || null,
      origin: bodyData.origin || null,
      path_lower: bodyData.path_lower || null,
      path_display: bodyData.path_display || null,
      timestamp: timestamp,
    };

    const putRequest = writeStore.put(dataToStore);

    putRequest.onsuccess = () => {
      savedCount++;
    };

    putRequest.onerror = () => {
      console.error(
        `[IndexedDB] ❌ Error saving ${eventType} body thumbnail ${bodyData.name}:`,
        putRequest.error
      );
      errorCount++;
    };
  });

  return new Promise((resolve, reject) => {
    writeTransaction.oncomplete = () => {
      resolve({ savedCount, errorCount, skippedCount });
    };

    writeTransaction.onerror = () => {
      console.error(
        "[IndexedDB] Transaction error for bulk admin body thumbnails:",
        writeTransaction.error
      );
      reject(writeTransaction.error);
    };
  });
};

/**
 * Save a body thumbnail to AdminBodies DB (shared library)
 * Only overwrites if: new data has thumbnail AND (modified timestamp is newer OR no existing entry)
 * This prevents cache corruption when server returns data without thumbnails (cache rebuilding)
 * @param {string} eventType - Event type (minimove/swingies/pixelme)
 * @param {object} bodyData - Body data with thumbnail
 */
export const saveAdminBodyThumbnail = async (eventType, bodyData) => {
  const storeName = getBodyThumbnailsStore(eventType);
  const db = await openAdminBodiesDB();

  // First, check if we should update (compare modified timestamps)
  const readTransaction = db.transaction(storeName, "readonly");
  const readStore = readTransaction.objectStore(storeName);
  const getRequest = readStore.get(bodyData.name);

  const existingData = await new Promise((resolve) => {
    getRequest.onsuccess = () => resolve(getRequest.result || null);
    getRequest.onerror = () => resolve(null);
  });

  // Decision logic for whether to update:
  // 1. Always update if no existing entry
  // 2. Always update if new data has thumbnail AND newer modified timestamp
  // 3. DON'T overwrite if new data has NO thumbnail (server cache rebuilding)
  // 4. DON'T overwrite if new modified equals existing (no actual change)

  const newHasThumbnail = Boolean(bodyData.thumbnail_data_uri);
  const existingHasThumbnail = Boolean(existingData?.thumbnail_data_uri);
  const newModified = bodyData.modified || null;
  const existingModified = existingData?.modified || null;

  let shouldUpdate = false;
  let updateReason = "";

  if (!existingData) {
    // No existing entry - only save if we have actual thumbnail data
    // Skip if server returned data without thumbnail (cache rebuilding scenario)
    shouldUpdate = newHasThumbnail;
    updateReason = newHasThumbnail ? "new_entry" : "skip_no_thumbnail";
  } else if (!newHasThumbnail && existingHasThumbnail) {
    // New data has no thumbnail but existing does - don't overwrite (server cache rebuilding)
    shouldUpdate = false;
    updateReason = "skip_no_thumbnail";
  } else if (
    newModified &&
    existingModified &&
    newModified !== existingModified
  ) {
    // Modified timestamp changed - update (image was changed, e.g., logo removed)
    shouldUpdate = true;
    updateReason = "modified_changed";
  } else if (newHasThumbnail && !existingHasThumbnail) {
    // New has thumbnail, existing doesn't - update
    shouldUpdate = true;
    updateReason = "add_thumbnail";
  } else if (!newModified && !existingModified) {
    // Both have no modified - update if new has thumbnail
    shouldUpdate = newHasThumbnail;
    updateReason = newHasThumbnail ? "refresh_no_timestamp" : "skip_no_data";
  } else {
    // Same modified timestamp, both have or don't have thumbnails - skip (no change)
    shouldUpdate = false;
    updateReason = "unchanged";
  }

  if (!shouldUpdate) {
    // console.log(`[IndexedDB] Skipping update for ${bodyData.name}: ${updateReason}`);
    return { updated: false, reason: updateReason };
  }

  // Proceed with update
  const writeTransaction = db.transaction(storeName, "readwrite");
  const writeStore = writeTransaction.objectStore(storeName);

  const dataToStore = {
    name: bodyData.name,
    thumbnail_data_uri: bodyData.thumbnail_data_uri || null,
    modified: bodyData.modified || null,
    origin: bodyData.origin || null,
    path_lower: bodyData.path_lower || null,
    path_display: bodyData.path_display || null,
    timestamp: new Date().toISOString(),
  };

  return new Promise((resolve, reject) => {
    const putRequest = writeStore.put(dataToStore);

    putRequest.onerror = (event) => {
      console.error(
        `[IndexedDB] Error saving ${eventType} body thumbnail ${bodyData.name}:`,
        event.target.errorCode
      );
    };

    writeTransaction.oncomplete = () => {
      // console.log(`[IndexedDB] Updated ${bodyData.name}: ${updateReason}`);
      resolve({ updated: true, reason: updateReason });
    };

    writeTransaction.onerror = (event) => {
      console.error(
        "[IndexedDB] Transaction error for admin body thumbnail:",
        event.target.errorCode
      );
      reject(event.target.errorCode);
    };
  });
};

/**
 * Get a single body thumbnail from AdminBodies DB
 * @param {string} eventType - Event type (minimove/swingies/pixelme)
 * @param {string} bodyName - Body name
 */
export const getAdminBodyThumbnail = async (eventType, bodyName) => {
  const storeName = getBodyThumbnailsStore(eventType);
  const db = await openAdminBodiesDB();
  const transaction = db.transaction(storeName, "readonly");
  const store = transaction.objectStore(storeName);
  const request = store.get(bodyName);

  return new Promise((resolve, reject) => {
    request.onsuccess = (event) => {
      const result = event.target.result || null;

      // Touch timestamp to extend TTL on access (non-blocking, don't await)
      if (result) {
        touchTimestamp(db, storeName, bodyName, result).catch((err) => {
          console.warn("Failed to touch timestamp:", err);
        });
      }

      resolve(result);
    };
    request.onerror = (event) => {
      console.error(
        `Error fetching ${eventType} body thumbnail:`,
        event.target.errorCode
      );
      reject(event.target.errorCode);
    };
  });
};

/**
 * Get all body thumbnails from AdminBodies DB for a specific event type
 * @param {string} eventType - Event type (minimove/swingies/pixelme)
 */
export const getAllAdminBodyThumbnails = async (eventType) => {
  const storeName = getBodyThumbnailsStore(eventType);
  const db = await openAdminBodiesDB();
  const transaction = db.transaction(storeName, "readonly");
  const store = transaction.objectStore(storeName);
  const request = store.getAll();

  return new Promise((resolve, reject) => {
    request.onsuccess = async (event) => {
      const results = event.target.result || [];

      // Touch timestamp for all accessed thumbnails to extend TTL
      if (results.length > 0) {
        try {
          // Update all timestamps in parallel (non-blocking)
          const touchPromises = results.map((item) =>
            touchTimestamp(db, storeName, item.name, item).catch((err) => {
              console.warn("Failed to touch timestamp for", item.name, err);
            })
          );

          // Don't await - let it run in background
          Promise.all(touchPromises).catch((err) => {
            console.warn("Some timestamp touches failed:", err);
          });
        } catch (err) {
          // Don't fail the read if touch fails
          console.warn("Failed to touch timestamps:", err);
        }
      }

      resolve(results);
    };
    request.onerror = (event) => {
      console.error(
        `Error fetching all ${eventType} body thumbnails:`,
        event.target.errorCode
      );
      reject(event.target.errorCode);
    };
  });
};

/**
 * @deprecated Use getAllAdminBodyThumbnails instead
 * Get all body thumbnails from IndexedDB (legacy function for backward compatibility)
 */
export const getAllBodyThumbnails = async (eventName) => {
  console.warn(
    "[Deprecated] getAllBodyThumbnails called - use getAllAdminBodyThumbnails(eventType) instead"
  );
  // Return empty array for backward compatibility
  return [];
};

// ============================================================
// INCREMENTAL SYNC - Body Library Synchronization
// ============================================================

/**
 * Simple hash function for fingerprint calculation (djb2 algorithm)
 * @param {string} str - String to hash
 * @returns {string} - Hash as hex string
 */
const hashString = (str) => {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
  }
  // Convert to unsigned 32-bit integer and then to hex
  return (hash >>> 0).toString(16);
};

/**
 * Calculate fingerprint of local AdminBodies data for comparison with server
 * Uses sorted name:modified pairs to detect any changes (additions, deletions, modifications)
 * @param {string} eventType - Event type (minimove/swingies)
 * @returns {Promise<{fingerprint: string, count: number}>} - Fingerprint hash and count
 */
export const getAdminBodiesFingerprint = async (eventType) => {
  try {
    const allBodies = await getAllAdminBodyThumbnails(eventType);

    // Build fingerprint from sorted name:modified pairs (same logic as server)
    const fingerprintParts = allBodies
      .map((body) => `${body.name || ""}:${body.modified || ""}`)
      .sort();

    const fingerprintString = fingerprintParts.join("|");

    // Use MD5-compatible hash for comparison with server
    // Note: Server uses Python's hashlib.md5, we use a simpler hash for client
    // The important thing is consistency within the same session
    const fingerprint = hashString(fingerprintString);

    return {
      fingerprint,
      count: allBodies.length,
      // Include raw data for debugging if needed
      _debug: {
        firstFew: fingerprintParts.slice(0, 3),
        lastFew: fingerprintParts.slice(-3),
      },
    };
  } catch (err) {
    console.error("[IndexedDB] Error calculating fingerprint:", err);
    return { fingerprint: "", count: 0 };
  }
};

/**
 * Sync AdminBodies DB with server data - handles additions, updates, AND deletions
 * This is the key function for incremental synchronization.
 *
 * Flow:
 * 1. Get all existing entries from IndexedDB
 * 2. Create a Set of names from server data
 * 3. For each item in IndexedDB not in server data → DELETE
 * 4. For each item from server:
 *    - If not in IndexedDB → ADD
 *    - If modified timestamp differs → UPDATE
 *
 * @param {string} eventType - Event type (minimove/swingies)
 * @param {Array} serverImages - Array of body data from server
 * @returns {Promise<{added: number, updated: number, deleted: number, unchanged: number}>}
 */
export const syncAdminBodyThumbnails = async (eventType, serverImages) => {
  if (!serverImages || !Array.isArray(serverImages)) {
    console.warn("[IndexedDB] syncAdminBodyThumbnails called with invalid data");
    return { added: 0, updated: 0, deleted: 0, unchanged: 0, errors: 0 };
  }

  const storeName = getBodyThumbnailsStore(eventType);
  const db = await openAdminBodiesDB();

  // Step 1: Get all existing entries from IndexedDB
  const readTransaction = db.transaction(storeName, "readonly");
  const readStore = readTransaction.objectStore(storeName);

  const existingEntries = await new Promise((resolve) => {
    const request = readStore.getAll();
    request.onsuccess = () => {
      const entries = request.result || [];
      const map = new Map();
      entries.forEach((entry) => {
        if (entry && entry.name) {
          map.set(entry.name, entry);
        }
      });
      resolve(map);
    };
    request.onerror = () => resolve(new Map());
  });

  // Step 2: Create a Set of names from server data
  const serverNamesSet = new Set(
    serverImages.filter((img) => img && img.name).map((img) => img.name)
  );

  // Step 3: Find entries to delete (in IndexedDB but not on server)
  const namesToDelete = [];
  for (const existingName of existingEntries.keys()) {
    if (!serverNamesSet.has(existingName)) {
      namesToDelete.push(existingName);
    }
  }

  // Step 4: Categorize server items (add/update/unchanged)
  const itemsToAdd = [];
  const itemsToUpdate = [];
  let unchangedCount = 0;

  // This store is SHARED across events, so it must hold the event-agnostic
  // (global) face position — never an event override. The event-selection
  // response provides global_face_position_ratio for that; the body-library
  // response has no event context, so its face_position_ratio IS global.
  const globalFacePosition = (item) =>
    item.global_face_position_ratio !== undefined
      ? item.global_face_position_ratio
      : item.face_position_ratio || null;

  for (const serverItem of serverImages) {
    if (!serverItem || !serverItem.name) continue;

    const existing = existingEntries.get(serverItem.name);
    const serverHasThumbnail = Boolean(serverItem.thumbnail_data_uri);

    if (!existing) {
      // New item - add if it has a thumbnail
      if (serverHasThumbnail) {
        itemsToAdd.push(serverItem);
      }
    } else {
      // Existing item - check if update needed
      const existingModified = existing.modified || "";
      const serverModified = serverItem.modified || "";
      const existingHasThumbnail = Boolean(existing.thumbnail_data_uri);
      // Global face-position edits touch only the positions JSON, not the
      // body file, so `modified` alone would never propagate them.
      const positionChanged =
        JSON.stringify(existing.face_position_ratio || null) !==
        JSON.stringify(globalFacePosition(serverItem));

      if (serverModified !== existingModified && serverHasThumbnail) {
        // Modified timestamp changed - update
        itemsToUpdate.push(serverItem);
      } else if (!existingHasThumbnail && serverHasThumbnail) {
        // Existing had no thumbnail but server has one now - update
        itemsToUpdate.push(serverItem);
      } else if (positionChanged) {
        // Keep the existing thumbnail if this payload happens to lack one.
        itemsToUpdate.push({
          ...serverItem,
          thumbnail_data_uri:
            serverItem.thumbnail_data_uri || existing.thumbnail_data_uri,
        });
      } else {
        // No change needed
        unchangedCount++;
      }
    }
  }

  // Step 5: Execute all changes in a single transaction
  const writeTransaction = db.transaction(storeName, "readwrite");
  const writeStore = writeTransaction.objectStore(storeName);
  const timestamp = new Date().toISOString();

  let deletedCount = 0;
  let addedCount = 0;
  let updatedCount = 0;
  let errorCount = 0;

  // Delete removed items
  for (const nameToDelete of namesToDelete) {
    try {
      writeStore.delete(nameToDelete);
      deletedCount++;
    } catch (err) {
      console.error(`[IndexedDB] Error deleting ${nameToDelete}:`, err);
      errorCount++;
    }
  }

  // Add new items
  for (const item of itemsToAdd) {
    try {
      const dataToStore = {
        name: item.name,
        thumbnail_data_uri: item.thumbnail_data_uri || null,
        modified: item.modified || null,
        origin: item.origin || null,
        path_lower: item.path_lower || null,
        path_display: item.path_display || null,
        face_position_ratio: globalFacePosition(item),
        global_face_position_ratio: globalFacePosition(item),
        timestamp: timestamp,
      };
      writeStore.put(dataToStore);
      addedCount++;
    } catch (err) {
      console.error(`[IndexedDB] Error adding ${item.name}:`, err);
      errorCount++;
    }
  }

  // Update modified items
  for (const item of itemsToUpdate) {
    try {
      const dataToStore = {
        name: item.name,
        thumbnail_data_uri: item.thumbnail_data_uri || null,
        modified: item.modified || null,
        origin: item.origin || null,
        path_lower: item.path_lower || null,
        path_display: item.path_display || null,
        face_position_ratio: globalFacePosition(item),
        global_face_position_ratio: globalFacePosition(item),
        timestamp: timestamp,
      };
      writeStore.put(dataToStore);
      updatedCount++;
    } catch (err) {
      console.error(`[IndexedDB] Error updating ${item.name}:`, err);
      errorCount++;
    }
  }

  // Wait for transaction to complete
  return new Promise((resolve, reject) => {
    writeTransaction.oncomplete = () => {
      const result = {
        added: addedCount,
        updated: updatedCount,
        deleted: deletedCount,
        unchanged: unchangedCount,
        errors: errorCount,
      };

      resolve(result);
    };

    writeTransaction.onerror = () => {
      console.error(
        "[IndexedDB] Transaction error during sync:",
        writeTransaction.error
      );
      reject(writeTransaction.error);
    };
  });
};

/**
 * Delete specific body thumbnails from AdminBodies IndexedDB
 * Used when bodies are moved to archive/garbage
 * @param {string} eventType - Event type (minimove/swingies)
 * @param {string[]} bodyNames - Array of body names to delete
 * @returns {Promise<{deleted: number, errors: number}>}
 */
export const deleteAdminBodyThumbnails = async (eventType, bodyNames) => {
  if (!bodyNames || !Array.isArray(bodyNames) || bodyNames.length === 0) {
    return { deleted: 0, errors: 0 };
  }

  const storeName = getBodyThumbnailsStore(eventType);
  const db = await openAdminBodiesDB();
  const transaction = db.transaction(storeName, "readwrite");
  const store = transaction.objectStore(storeName);

  let deletedCount = 0;
  let errorCount = 0;

  for (const name of bodyNames) {
    try {
      store.delete(name);
      deletedCount++;
    } catch (err) {
      console.error(`[IndexedDB] Error deleting ${name}:`, err);
      errorCount++;
    }
  }

  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => {
      resolve({ deleted: deletedCount, errors: errorCount });
    };

    transaction.onerror = () => {
      console.error(
        "[IndexedDB] Transaction error during delete:",
        transaction.error
      );
      reject(transaction.error);
    };
  });
};

/**
 * Add specific body thumbnails to AdminBodies IndexedDB
 * Used when bodies are restored from archive/garbage
 * @param {string} eventType - Event type (minimove/swingies)
 * @param {Array} bodies - Array of body objects with thumbnail_data_uri
 * @returns {Promise<{added: number, errors: number}>}
 */
export const addAdminBodyThumbnails = async (eventType, bodies) => {
  if (!bodies || !Array.isArray(bodies) || bodies.length === 0) {
    return { added: 0, errors: 0 };
  }

  const storeName = getBodyThumbnailsStore(eventType);
  const db = await openAdminBodiesDB();
  const transaction = db.transaction(storeName, "readwrite");
  const store = transaction.objectStore(storeName);
  const timestamp = new Date().toISOString();

  let addedCount = 0;
  let errorCount = 0;

  for (const body of bodies) {
    // Skip bodies without thumbnails
    if (!body.thumbnail_data_uri && !body.thumbnail_base64) {
      continue;
    }

    try {
      // Build thumbnail_data_uri if only base64 is available
      let thumbnailDataUri = body.thumbnail_data_uri;
      if (!thumbnailDataUri && body.thumbnail_base64) {
        const contentType = body.thumbnail_content_type || "image/webp";
        thumbnailDataUri = `data:${contentType};base64,${body.thumbnail_base64}`;
      }

      const dataToStore = {
        name: body.name,
        thumbnail_data_uri: thumbnailDataUri,
        modified: body.modified || null,
        origin: body.origin || null,
        path_lower: body.path_lower || null,
        path_display: body.path_display || body.path || null,
        timestamp: timestamp,
      };
      store.put(dataToStore);
      addedCount++;
    } catch (err) {
      console.error(`[IndexedDB] Error adding ${body.name}:`, err);
      errorCount++;
    }
  }

  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => {
      resolve({ added: addedCount, errors: errorCount });
    };

    transaction.onerror = () => {
      console.error(
        "[IndexedDB] Transaction error during add:",
        transaction.error
      );
      reject(transaction.error);
    };
  });
};

/**
 * Save body metadata (selection mode, logos per body, etc.)
 * @param {string} eventName - Event folder name
 * @param {object} metadata - Metadata object
 * @param {string} metadata.selection_mode - Selection mode (ALL/CUSTOM/NONE)
 * @param {string[]} metadata.selected_body_names - Array of selected body names
 * @param {string} metadata.body_template_id - Template ID
 * @param {object} metadata.body_logos - Map of body names to logo configs {bodyName: {has_logo, logo_config, logo_applied_at}}
 * @param {object} metadata.body_face_overrides - Map of body names to MiniMove per-event head positions {bodyName: {x, y}}
 */
export const saveBodyMetadata = async (eventName, metadata) => {
  const db = await openDatabase(eventName);
  const transaction = db.transaction(BODY_METADATA_STORE, "readwrite");
  const store = transaction.objectStore(BODY_METADATA_STORE);

  const dataToStore = {
    id: "metadata", // Fixed key
    etag: metadata.etag || null,
    selection_mode: metadata.selection_mode || "ALL",
    selected_body_names: metadata.selected_body_names || [],
    body_template_id: metadata.body_template_id || "",
    body_logos: metadata.body_logos || {}, // Map: {bodyName: {has_logo, logo_config, logo_applied_at}}
    body_face_overrides: metadata.body_face_overrides || {}, // Map: {bodyName: {x, y}}
    last_refresh: new Date().toISOString(),
    total_bodies: metadata.total_bodies || 0,
  };

  return new Promise((resolve, reject) => {
    const putRequest = store.put(dataToStore);

    putRequest.onerror = (event) => {
      console.error(
        "[IndexedDB] Error saving metadata:",
        event.target.errorCode
      );
    };

    transaction.oncomplete = () => {
      resolve(true);
    };

    transaction.onerror = (event) => {
      console.error(
        "[IndexedDB] Metadata transaction error:",
        event.target.errorCode
      );
      reject(event.target.errorCode);
    };
  });
};

/**
 * Get body metadata from IndexedDB
 */
export const getBodyMetadata = async (eventName) => {
  const db = await openDatabase(eventName);
  const transaction = db.transaction(BODY_METADATA_STORE, "readonly");
  const store = transaction.objectStore(BODY_METADATA_STORE);
  const request = store.get("metadata");

  return new Promise((resolve, reject) => {
    request.onsuccess = async (event) => {
      const result = event.target.result || null;

      // Touch timestamp to extend TTL on access
      if (result) {
        try {
          // Update last_refresh field (metadata uses this instead of timestamp)
          const updatedMetadata = {
            ...result,
            last_refresh: new Date().toISOString(),
          };

          const touchTransaction = db.transaction(
            BODY_METADATA_STORE,
            "readwrite"
          );
          const touchStore = touchTransaction.objectStore(BODY_METADATA_STORE);
          touchStore.put(updatedMetadata);

          // Don't wait for completion - non-blocking
          touchTransaction.oncomplete = () => {
            // Silent success
          };
          touchTransaction.onerror = (err) => {
            console.warn("Failed to touch metadata timestamp:", err);
          };
        } catch (err) {
          // Don't fail the read if touch fails
          console.warn("Failed to touch metadata timestamp:", err);
        }
      }

      resolve(result);
    };
    request.onerror = (event) => {
      console.error("Error fetching body metadata:", event.target.errorCode);
      reject(event.target.errorCode);
    };
  });
};

/**
 * Save general metadata (ETag, timestamp) for any store
 */
export const saveMetadata = async (eventName, storeName, metadata) => {
  const db = await openDatabase(eventName);
  const transaction = db.transaction(METADATA_STORE_NAME, "readwrite");
  const store = transaction.objectStore(METADATA_STORE_NAME);

  const dataToStore = {
    store_name: storeName,
    etag: metadata.etag || null,
    last_modified: new Date().toISOString(),
    item_count: metadata.item_count || 0,
  };

  return new Promise((resolve, reject) => {
    store.put(dataToStore);
    transaction.oncomplete = () => resolve(true);
    transaction.onerror = (event) => {
      console.error("Error saving metadata:", event.target.errorCode);
      reject(event.target.errorCode);
    };
  });
};

/**
 * Get general metadata for a store
 */
export const getMetadata = async (eventName, storeName) => {
  const db = await openDatabase(eventName);
  const transaction = db.transaction(METADATA_STORE_NAME, "readonly");
  const store = transaction.objectStore(METADATA_STORE_NAME);
  const request = store.get(storeName);

  return new Promise((resolve, reject) => {
    request.onsuccess = (event) => {
      resolve(event.target.result || null);
    };
    request.onerror = (event) => {
      console.error("Error fetching metadata:", event.target.errorCode);
      reject(event.target.errorCode);
    };
  });
};

// ============================================================
// EVENT-SPECIFIC BODY THUMBNAILS (Logos & One-Time Images)
// ============================================================

/**
 * Save multiple event-specific body thumbnails (logos and one-time images)
 * These are stored in the event-specific database, NOT the shared AdminBodies DB
 * @param {string} eventName - Event folder name
 * @param {Array} bodyDataArray - Array of body data objects with thumbnails
 */
export const saveEventBodyThumbnails = async (eventName, bodyDataArray) => {
  if (!bodyDataArray || bodyDataArray.length === 0) {
    console.warn("[IndexedDB] saveEventBodyThumbnails called with empty array");
    return { savedCount: 0, errorCount: 0 };
  }

  const db = await openDatabase(eventName);
  const transaction = db.transaction(EVENT_BODY_THUMBNAILS_STORE, "readwrite");
  const store = transaction.objectStore(EVENT_BODY_THUMBNAILS_STORE);

  const timestamp = new Date().toISOString();
  let savedCount = 0;
  let errorCount = 0;

  // Queue all put operations
  bodyDataArray.forEach((bodyData) => {
    if (!bodyData || !bodyData.name) {
      errorCount++;
      return;
    }

    const dataToStore = {
      name: bodyData.name,
      thumbnail_data_uri: bodyData.thumbnail_data_uri || null,
      modified: bodyData.modified || null,
      origin: bodyData.origin || null,
      has_logo: bodyData.has_logo || false,
      logo_config: bodyData.logo_config || null,
      logo_applied_at: bodyData.logo_applied_at || null,
      path_lower: bodyData.path_lower || null,
      path_display: bodyData.path_display || null,
      face_position_ratio: bodyData.face_position_ratio || null,
      global_face_position_ratio: bodyData.global_face_position_ratio || null,
      timestamp: timestamp,
    };

    const putRequest = store.put(dataToStore);

    putRequest.onsuccess = () => {
      savedCount++;
    };

    putRequest.onerror = () => {
      console.error(
        `[IndexedDB] ❌ Error saving event-specific body thumbnail ${bodyData.name}:`,
        putRequest.error
      );
      errorCount++;
    };
  });

  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => {
      resolve({ savedCount, errorCount });
    };

    transaction.onerror = () => {
      console.error(
        "[IndexedDB] Transaction error for event-specific body thumbnails:",
        transaction.error
      );
      reject(transaction.error);
    };
  });
};

/**
 * Save a single event-specific body thumbnail (logo or one-time image)
 * @param {string} eventName - Event folder name
 * @param {object} bodyData - Body data with thumbnail
 */
export const saveEventBodyThumbnail = async (eventName, bodyData) => {
  const db = await openDatabase(eventName);
  const transaction = db.transaction(EVENT_BODY_THUMBNAILS_STORE, "readwrite");
  const store = transaction.objectStore(EVENT_BODY_THUMBNAILS_STORE);

  const dataToStore = {
    name: bodyData.name,
    thumbnail_data_uri: bodyData.thumbnail_data_uri || null,
    modified: bodyData.modified || null,
    origin: bodyData.origin || null,
    has_logo: bodyData.has_logo || false,
    logo_config: bodyData.logo_config || null,
    logo_applied_at: bodyData.logo_applied_at || null,
    path_lower: bodyData.path_lower || null,
    path_display: bodyData.path_display || null,
    face_position_ratio: bodyData.face_position_ratio || null,
    global_face_position_ratio: bodyData.global_face_position_ratio || null,
    timestamp: new Date().toISOString(),
  };

  return new Promise((resolve, reject) => {
    const putRequest = store.put(dataToStore);

    putRequest.onerror = (event) => {
      console.error(
        `[IndexedDB] Error saving event-specific body thumbnail ${bodyData.name}:`,
        event.target.errorCode
      );
    };

    transaction.oncomplete = () => {
      resolve({ updated: true });
    };

    transaction.onerror = (event) => {
      console.error(
        "[IndexedDB] Transaction error for event-specific body thumbnail:",
        event.target.errorCode
      );
      reject(event.target.errorCode);
    };
  });
};

/**
 * Get all event-specific body thumbnails (logos and one-time images)
 * @param {string} eventName - Event folder name
 */
export const getAllEventBodyThumbnails = async (eventName) => {
  const db = await openDatabase(eventName);
  const transaction = db.transaction(EVENT_BODY_THUMBNAILS_STORE, "readonly");
  const store = transaction.objectStore(EVENT_BODY_THUMBNAILS_STORE);
  const request = store.getAll();

  return new Promise((resolve, reject) => {
    request.onsuccess = async (event) => {
      const results = event.target.result || [];

      // Touch timestamp for all accessed thumbnails to extend TTL
      if (results.length > 0) {
        try {
          const touchPromises = results.map((item) =>
            touchTimestamp(db, EVENT_BODY_THUMBNAILS_STORE, item.name, item).catch((err) => {
              console.warn("Failed to touch timestamp for", item.name, err);
            })
          );

          // Don't await - let it run in background
          Promise.all(touchPromises).catch((err) => {
            console.warn("Some timestamp touches failed:", err);
          });
        } catch (err) {
          console.warn("Failed to touch timestamps:", err);
        }
      }

      resolve(results);
    };
    request.onerror = (event) => {
      console.error(
        `Error fetching all event-specific body thumbnails:`,
        event.target.errorCode
      );
      reject(event.target.errorCode);
    };
  });
};

/**
 * Delete a single event-specific body thumbnail
 * Used when a logo is removed from a body
 * @param {string} eventName - Event folder name
 * @param {string} bodyName - Body name to delete
 */
export const deleteEventBodyThumbnail = async (eventName, bodyName) => {
  const db = await openDatabase(eventName);
  const transaction = db.transaction(EVENT_BODY_THUMBNAILS_STORE, "readwrite");
  const store = transaction.objectStore(EVENT_BODY_THUMBNAILS_STORE);

  return new Promise((resolve, reject) => {
    const deleteRequest = store.delete(bodyName);

    deleteRequest.onsuccess = () => {
      resolve(true);
    };

    deleteRequest.onerror = (event) => {
      console.error(
        `[IndexedDB] Error deleting event-specific thumbnail ${bodyName}:`,
        event.target.errorCode
      );
      reject(event.target.errorCode);
    };
  });
};

/**
 * Clean up old body thumbnails based on TTL
 */
/**
 * Clean up old body thumbnails from AdminBodies DB (all event types)
 * @param {string} eventType - Optional: specific event type to clean, or null for all
 */
export const cleanUpOldBodyThumbnails = async (
  eventType = null,
  ttl = CACHE_TTL.ADMIN
) => {
  // console.log("[cleanUpOldBodyThumbnails] Cleaning AdminBodies DB");

  const db = await openAdminBodiesDB();
  const now = new Date();

  // Determine which stores to clean
  const storesToClean = eventType
    ? [getBodyThumbnailsStore(eventType)]
    : [BODY_THUMBNAILS_MINIMOVE, BODY_THUMBNAILS_SWINGIES];

  const cleanupAllStores = async () => {
    for (const storeName of storesToClean) {
      // console.log(`[cleanUpOldBodyThumbnails] Cleaning store: ${storeName}`);
      const transaction = db.transaction(storeName, "readwrite");
      const store = transaction.objectStore(storeName);
      const allKeysRequest = store.getAllKeys();

      await new Promise((resolve, reject) => {
        allKeysRequest.onsuccess = async (event) => {
          const allKeys = event.target.result;
          const cleanupPromises = allKeys.map((key) => {
            return new Promise((res, rej) => {
              const getRequest = store.get(key);
              getRequest.onsuccess = (e) => {
                const entry = e.target.result;
                if (entry && entry.timestamp) {
                  const entryTime = new Date(entry.timestamp);
                  if (now - entryTime > ttl) {
                    const deleteRequest = store.delete(key);
                    deleteRequest.onsuccess = () => res(true);
                    deleteRequest.onerror = (err) => rej(err.target.errorCode);
                  } else {
                    res(true);
                  }
                } else {
                  res(false);
                }
              };
              getRequest.onerror = (err) => rej(err.target.errorCode);
            });
          });

          try {
            await Promise.all(cleanupPromises);
            resolve(true);
          } catch (error) {
            console.error(`Error during ${storeName} cleanup:`, error);
            reject(error);
          }
        };

        allKeysRequest.onerror = (event) => {
          console.error(
            `Failed to fetch keys from ${storeName}:`,
            event.target.errorCode
          );
          reject(event.target.errorCode);
        };
      });
    }
  };

  return cleanupAllStores();
};

/**
 * Clear body cache for an event
 * Clears metadata and event-specific thumbnails (logos, one-time images)
 * NOTE: Does NOT clear shared AdminBodies DB - those are shared across all events
 */
export const clearBodyCache = async (eventName) => {
  // console.log("[clearBodyCache] Clearing body cache for event:", eventName);

  const db = await openDatabase(eventName);

  // Clear metadata AND event-specific thumbnails
  const transaction = db.transaction(
    [BODY_METADATA_STORE, EVENT_BODY_THUMBNAILS_STORE, METADATA_STORE_NAME],
    "readwrite"
  );

  const metadataStore = transaction.objectStore(BODY_METADATA_STORE);
  const eventThumbnailsStore = transaction.objectStore(EVENT_BODY_THUMBNAILS_STORE);
  const generalMetadataStore = transaction.objectStore(METADATA_STORE_NAME);

  return new Promise((resolve, reject) => {
    // Clear metadata
    metadataStore.clear();

    // Clear event-specific thumbnails (logos, one-time images)
    eventThumbnailsStore.clear();

    // Clear body-related general metadata
    generalMetadataStore.delete(BODY_METADATA_STORE);
    generalMetadataStore.delete(EVENT_BODY_THUMBNAILS_STORE);

    transaction.oncomplete = () => {
      // console.log("Body cache cleared for event:", eventName);
      resolve(true);
    };
    transaction.onerror = (event) => {
      console.error("Error clearing body cache:", event.target.errorCode);
      reject(event.target.errorCode);
    };
  });
};

/**
 * Clear face images from the event database
 */
export const clearFaceCache = async (eventName) => {
  const db = await openDatabase(eventName);
  const transaction = db.transaction(
    [FACE_STORE_NAME, FACE_THUMBNAILS_STORE, METADATA_STORE_NAME],
    "readwrite"
  );

  const faceStore = transaction.objectStore(FACE_STORE_NAME);
  const faceThumbnailsStore = transaction.objectStore(FACE_THUMBNAILS_STORE);
  const generalMetadataStore = transaction.objectStore(METADATA_STORE_NAME);

  return new Promise((resolve, reject) => {
    faceStore.clear();
    faceThumbnailsStore.clear();

    // Only clear face-related metadata
    generalMetadataStore.delete(FACE_STORE_NAME);

    transaction.oncomplete = () => {
      // console.log("Face cache cleared");
      resolve(true);
    };
    transaction.onerror = (event) => {
      console.error("Error clearing face cache:", event.target.errorCode);
      reject(event.target.errorCode);
    };
  });
};

/**
 * Clear attraction-related stores for inactive events
 * Keeps ResultThumbnails and MetadataStore intact
 * Note: BodyThumbnails are in AdminBodies DB (shared), not cleared here
 */
export const clearAttractionStores = async (eventName) => {
  try {
    const db = await openDatabase(eventName);

    // Only clear attraction-specific stores (faces and results)
    // DO NOT clear BODY_METADATA_STORE - it contains selection/logo info that should persist!
    const stores = [FACE_STORE_NAME, FACE_THUMBNAILS_STORE, RESULTS_STORE_NAME];

    const transaction = db.transaction(
      [...stores, METADATA_STORE_NAME],
      "readwrite"
    );

    // Clear each attraction-related store
    stores.forEach((storeName) => {
      if (db.objectStoreNames.contains(storeName)) {
        const store = transaction.objectStore(storeName);
        store.clear();
      }
    });

    // Clear metadata for attraction stores
    const metadataStore = transaction.objectStore(METADATA_STORE_NAME);
    stores.forEach((storeName) => {
      metadataStore.delete(storeName);
    });

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => {
        // console.log(`Cleared attraction stores for event: ${eventName}`);
        resolve(true);
      };
      transaction.onerror = (event) => {
        console.error(
          "Error clearing attraction stores:",
          event.target.errorCode
        );
        reject(event.target.errorCode);
      };
    });
  } catch (error) {
    console.error("Failed to clear attraction stores:", error);
    throw error;
  }
};

/**
 * Clear all IndexedDB databases in the browser
 * WARNING: This deletes ALL data stored in IndexedDB
 */
export const clearAllIndexedDB = async () => {
  try {
    // Get list of all databases
    const databases = await indexedDB.databases();
    const deletePromises = [];

    for (const dbInfo of databases) {
      if (dbInfo.name) {
        deletePromises.push(deleteDatabase(dbInfo.name));
      }
    }

    await Promise.all(deletePromises);
    return {
      success: true,
      deletedCount: databases.length,
      deletedDatabases: databases.map((db) => db.name),
    };
  } catch (error) {
    console.error("Failed to clear all IndexedDB:", error);
    throw error;
  }
};
